import * as Sentry from '@sentry/react';
import buildInfo from '@root/build-helpers/build-info.json';
import { getSimplifiedGatesFromStore } from '@store/feature-gates.store';
import { getEnvironmentName } from '@util/helpers/bootstrap.helpers';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// --- Sanitization Helpers (ported from bootstrap.helpers.js) ---
const SENSITIVE_KEYS = [
  'password',
  'token',
  'secret',
  'apikey',
  'api_key',
  'auth',
  'authorization',
  'cookie',
  'set-cookie',
  'email',
  'creditcard',
  'credit_card',
  'ccnum',
  'cvv',
  'ssn'
];

const SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'proxy-authorization',
  'x-fora-key'
];

const REDACTED_PLACEHOLDER = '[REDACTED]';
const MAX_VALUE_LENGTH = 2048;

const EMAIL_REGEX = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

/**
 * Recursively sanitizes an input value (object, array, or primitive).
 * Redacts strings matching email patterns and keys/values identified as sensitive.
 * Truncates long strings and handles circular references.
 */
export function sanitizeValue(value, path = '', visited = new Set()) {
  if (value === null || typeof value !== 'object') return handlePrimitiveValue(value);
  if (visited.has(value)) return '[CIRCULAR_REFERENCE]';

  visited.add(value);

  let sanitizedData;
  if (Array.isArray(value)) {
    sanitizedData = value.map((item, index) => sanitizeValue(item, `${path}[${index}]`, visited));
  } else {
    sanitizedData = {};
    for (const key in value) {
      if (Object.hasOwn(value, key)) {
        const lowerKey = key.toLowerCase();
        const currentPath = path ? `${path}.${key}` : key;

        if (SENSITIVE_KEYS.includes(lowerKey)) {
          sanitizedData[key] = REDACTED_PLACEHOLDER;
        } else {
          sanitizedData[key] = sanitizeValue(value[key], currentPath, visited);
        }
      }
    }
  }

  visited.delete(value);
  return sanitizedData;
}

// Helper function for sanitizeValue
function handlePrimitiveValue(value) {
  if (typeof value !== 'string') return value;

  let sanitized = value.replace(EMAIL_REGEX, REDACTED_PLACEHOLDER);

  if (sanitized.length > MAX_VALUE_LENGTH) {
    sanitized = sanitized.substring(0, MAX_VALUE_LENGTH) + '...[TRUNCATED]';
  }

  return sanitized;
}

/**
 * Sanitizes HTTP headers by redacting sensitive header values.
 */
export function sanitizeHeaders(headers) {
  if (!headers || typeof headers !== 'object') {
    return headers;
  }
  const sanitized = {};
  for (const key in headers) {
    if (Object.hasOwn(headers, key)) {
      const lowerKey = key.toLowerCase();
      if (SENSITIVE_HEADERS.includes(lowerKey)) {
        sanitized[key] = REDACTED_PLACEHOLDER;
      } else {
        sanitized[key] = sanitizeValue(headers[key], `header.${key}`);
      }
    }
  }
  return sanitized;
}

// --- Sentry Transform Helper Functions ---

/**
 * Applies a standardized title to the Sentry event.
 * Looks for a title format like `[TYPE] Identifier` in the error message or context.
 */
export function applySentryCustomTitle(event, hint) {
  let customTitle = null;

  // Check if the original error has a formatted message
  if (hint?.originalException?.message?.startsWith('[')) {
    customTitle = hint.originalException.message;
  } else if (event?.exception?.values?.[0]?.value?.startsWith('[')) {
    customTitle = event.exception.values[0].value;
  } else if (event?.message?.message?.startsWith('[')) {
    customTitle = event.message.message;
  }

  if (customTitle) {
    // Set the event message
    if (!event.message) {
      event.message = customTitle;
    }

    // Update exception values
    if (event.exception?.values) {
      event.exception.values.forEach((exception) => {
        exception.value = customTitle;
      });
    }

    // Store in context for reference
    if (!event.contexts) event.contexts = {};
    event.contexts.error_classification = {
      formatted_title: customTitle,
      title_applied: true
    };
  }

  return event;
}

/**
 * Sanitizes request data in the Sentry event.
 * Redacts sensitive fields in request payload and headers.
 */
export function sanitizeSentryEvent(event) {
  // Sanitize request data
  if (event.request?.data) {
    event.request.data = sanitizeValue(event.request.data);
  }

  // Sanitize request headers
  if (event.request?.headers) {
    event.request.headers = sanitizeHeaders(event.request.headers);
  }

  // Sanitize exception stack trace variables
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((exception) => ({
      ...exception,
      stacktrace: exception.stacktrace
        ? {
            ...exception.stacktrace,
            frames: exception.stacktrace.frames?.map((frame) => ({
              ...frame,
              vars: frame.vars ? sanitizeValue(frame.vars) : frame.vars
            }))
          }
        : exception.stacktrace
    }));
  }

  // Sanitize additional data
  if (event.extra) {
    event.extra = sanitizeValue(event.extra);
  }

  return event;
}

/**
 * Adds build information and environment to the Sentry event.
 */
export function addSentryBuildInfo(event) {
  if (!event.contexts) event.contexts = {};

  event.contexts.build_info = buildInfo
    ? { ...buildInfo, environment: getEnvironmentName() }
    : { environment: getEnvironmentName() };

  // Add build info as tags for easy filtering
  event.tags = {
    ...event.tags,
    version: buildInfo?.Version || 'unknown',
    commit_hash: buildInfo?.['Commit Hash'] || 'unknown',
    environment: getEnvironmentName()
  };

  return event;
}

/**
 * Merges additional context data into the Sentry event.
 */
export function mergeSentryContext(event, hint) {
  // If there's additional context in the hint, merge it
  if (hint?.contexts) {
    if (!event.contexts) event.contexts = {};
    event.contexts = { ...event.contexts, ...hint.contexts };
  }

  // Add feature flags context
  try {
    const simplifiedFlags = getSimplifiedGatesFromStore();
    if (simplifiedFlags && Object.keys(simplifiedFlags).length > 0) {
      if (!event.contexts) event.contexts = {};
      event.contexts.feature_flags = simplifiedFlags;
    }
  } catch (flagError) {
    console.error('[Sentry Transform] Failed to get feature flags:', flagError);
  }

  return event;
}

/**
 * Removes potentially large Statsig client instances from the event data.
 */
export function removeSentryStatsigClient(event) {
  // Remove from contexts
  if (event.contexts) {
    for (const contextKey in event.contexts) {
      if (event.contexts[contextKey]?.statsigClient) {
        delete event.contexts[contextKey].statsigClient;
      }
    }
  }

  // Remove from extra data
  if (event.extra) {
    for (const key in event.extra) {
      if (event.extra[key]?.statsigClient) {
        delete event.extra[key].statsigClient;
      }
    }
  }

  return event;
}

/**
 * Truncates long component stack traces to prevent excessive data.
 */
export function truncateSentryComponentStack(event) {
  if (event.contexts?.react?.componentStack && event.contexts.react.componentStack.length > 300) {
    event.contexts.react.componentStack =
      event.contexts.react.componentStack.substring(0, 300) + '... (truncated)';
  }

  // Also check in extra data
  if (event.extra?.componentStack && event.extra.componentStack.length > 300) {
    event.extra.componentStack = event.extra.componentStack.substring(0, 300) + '... (truncated)';
  }

  return event;
}

/**
 * Main transform pipeline that applies all transformations to the Sentry event.
 */
export function applySentryTransformPipeline(event, hint) {
  event = applySentryCustomTitle(event, hint);
  event = sanitizeSentryEvent(event);
  event = addSentryBuildInfo(event);
  event = mergeSentryContext(event, hint);
  event = removeSentryStatsigClient(event);
  event = truncateSentryComponentStack(event);

  return event;
}

/**
 * Determines whether a Sentry event should be ignored.
 * Equivalent to Rollbar's checkIgnore function.
 */
export function shouldIgnoreError(event, hint) {
  const originalError = hint?.originalException;

  // Ignore errors already reported by interceptor
  if (originalError?._reportedByInterceptor) {
    return true;
  }

  const errorMsg =
    event?.exception?.values?.[0]?.value ||
    event?.message?.message ||
    event?.message ||
    originalError?.message ||
    '';

  // Ignore test errors
  if (errorMsg.includes('Test:') || errorMsg.includes('should be caught')) {
    console.log('[Sentry Ignore] Ignoring test error:', errorMsg);
    return true;
  }

  // Ignore 403 errors (handled by interceptor)
  if (errorMsg.includes('403')) {
    return true;
  }

  // Ignore likely duplicate uncaught errors without formatted titles
  const hasFormattedTitle = errorMsg.startsWith('[');
  const isUncaught = hint?.mechanism?.handled === false;

  if (isUncaught && !hasFormattedTitle) {
    console.log(
      '[Sentry Ignore] Ignoring likely duplicate uncaught error (lacks formatted title):',
      errorMsg
    );
    return true;
  }

  return false;
}

// --- Sentry Configuration ---

/**
 * Creates Sentry configuration object based on current environment.
 * This is a function to avoid circular import issues.
 */
function createSentryConfig() {
  const currentEnv = getEnvironmentName();

  return {
    dsn: SENTRY_DSN,
    environment: currentEnv,
    enabled: currentEnv !== 'local',

    // Performance and sampling
    tracesSampleRate: currentEnv === 'production' ? 0.1 : 1.0,
    profilesSampleRate: currentEnv === 'production' ? 0.1 : 1.0,

    // Release and source maps
    release: buildInfo?.['Commit Hash'] || 'unknown',

    // Integration configuration
    integrations: [
      // Core integrations are automatically included
    ],

    // Error filtering and transformation - equivalent to Rollbar's transform + checkIgnore
    beforeSend: (event, hint) => {
      // Apply ignore logic first
      if (shouldIgnoreError(event, hint)) {
        return null;
      }

      // Apply transformation pipeline
      event = applySentryTransformPipeline(event, hint);

      return event;
    },

    // Initial context (equivalent to Rollbar's payload defaults)
    initialScope: {
      tags: {
        version: buildInfo?.Version || 'unknown',
        environment: currentEnv,
        commit_hash: buildInfo?.['Commit Hash'] || 'unknown'
      },
      contexts: {
        build: buildInfo || {}
      }
    }
  };
}

/**
 * Initialize Sentry with the configured options.
 */
export function initializeSentry() {
  const sentryConfig = createSentryConfig();

  if (sentryConfig.enabled && SENTRY_DSN) {
    Sentry.init(sentryConfig);
    console.log('[Sentry] Initialized for environment:', sentryConfig.environment);
  } else {
    console.log('[Sentry] Disabled for local environment or missing DSN');
  }
}

/**
 * Set user context in Sentry (equivalent to registerUserWithRollbar).
 */
export function registerUserWithSentry(user) {
  if (user?.id) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name
    });

    // Set additional user context
    Sentry.setContext('user_details', {
      type: user.type,
      isGuest: user.isGuest,
      tenants: user.tenants?.map((t) => t.name)
    });

    console.log('[Sentry] User configured:', user.id);
  } else {
    Sentry.setUser(null);
    console.log('[Sentry] User context cleared.');
  }
}

/**
 * Clear user context from Sentry (equivalent to clearUserFromRollbar).
 */
export function clearUserFromSentry() {
  Sentry.setUser(null);
  console.log('[Sentry] User context explicitly cleared.');
}
